import { Routes, Route, Navigate, Outlet, useLocation, useParams } from 'react-router';
import { lazy, useEffect, useRef, useState } from 'react';
import { Layout } from '@/components/Layout/Layout.tsx';
import { GetStartedPage } from '@/pages/GetStarted/GetStartedPage.tsx';
import { isAccountSetupIncomplete } from '@/helpers/profileHelpers';
import { UserModel } from '@/apiTypes/UserTypes';
import { useUser } from '@/hooks/user/useUser.ts';
import { ProtectedRoute } from '@/components/ProtectedRoute.tsx';
import { PublicRoute } from '@/components/PublicRoute.tsx';
import { NotFound } from '@/components/NotFound/NotFound.tsx';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { SkillsProvider } from '@/pages/LegacySkills/SkillsContext/SkillsProviderContext';
import { FeatureFlagOutletGate } from '@/components/FeatureFlagOutletGate';
import { FeatureAccessOutletGate } from '@/components/FeatureAccessOutletGate';
import { DemoRoute } from './DemoRoute';
import { NoProdRoute } from './NoProdRoute';
import { navigateToLogin, navigateToLogout } from '@/lib/loginUrl';
import { shouldShowPartnerLanding } from '@/lib/partnerLandingUrl';
import { shouldShowReferralRockLanding } from '@/lib/referralRockLandingUrl';
import { useHydrated } from '@/ssr/ssrHydration';

/**
 * Redirects to `to` while carrying the query string over, so deep links such as
 * `/resources?page=3` keep their state across the `/library` rename.
 */
function RedirectPreservingSearch({ to }: { to: string }) {
    const { search } = useLocation();

    return <Navigate to={{ pathname: to, search }} replace />;
}

/** Legacy `/articles/:slug` and `/resources/articles/:slug` → `/library/articles/:slug`. */
function RedirectLegacyArticleDetail() {
    const { slug } = useParams<{ slug: string }>();
    const { search } = useLocation();
    const pathname = slug ? `/library/articles/${encodeURIComponent(slug)}` : '/library';

    return <Navigate to={{ pathname, search }} replace />;
}

/**
 * Full-page hand-offs to the API's sign-in and sign-out. Declared at module scope so their identity
 * survives `AppRoutes` re-renders: declared inside it, every post-mount auth-state update remounted
 * them and ran the effect again, sending a second `/api/login` that staged a second OAuth state and
 * could leave the `state` cookie out of step with the state Auth0 returns.
 *
 * React can also run a mount effect again for the same instance (StrictMode in development, or a
 * Suspense boundary hiding and revealing it), so each one guards with a ref to navigate once.
 */
function LoginRedirect() {
    const handedOff = useRef(false);
    useEffect(() => {
        if (handedOff.current) {
            return;
        }
        handedOff.current = true;
        navigateToLogin();
    }, []);

    return null;
}

/**
 * `/logout` hands off to `/api/logout` whatever the auth state, including part-way through account
 * setup: the API clears whatever session the request carries and redirects, and has nothing to do
 * when there is none.
 */
function LogoutRedirect() {
    const handedOff = useRef(false);
    useEffect(() => {
        if (handedOff.current) {
            return;
        }
        handedOff.current = true;
        navigateToLogout();
    }, []);

    return null;
}

/**
 * `/get-started` in the registered-user route table. A user who finished account setup has nothing
 * to do there (back button, old bookmark), so send them to the dashboard. A user still in setup
 * never reaches this: `ProtectedRoute` waits until the user is known, and an incomplete account
 * gets the setup route table instead. The check repeats that rule here so this route can never
 * send someone who still needs the form away from it.
 */
function RegisteredGetStartedRedirect({ user }: { user: UserModel | undefined }) {
    if (user?.id && !isAccountSetupIncomplete(user)) {
        return <Navigate to="/dashboard" replace />;
    }

    return <GetStartedPage />;
}

const PrivacyPolicy = lazy(() =>
    import('@/pages/PrivacyPolicy/PrivacyPolicy.tsx').then(m => ({ default: m.PrivacyPolicy })),
);
const TermsAndConditions = lazy(() =>
    import('@/pages/TermsAndConditions/TermsAndConditions.tsx').then(m => ({ default: m.TermsAndConditions })),
);
const StyleGuide = lazy(() =>
    import('@/pages/StyleGuide/StyleGuide.tsx').then(m => ({ default: m.StyleGuide })),
);
const CmsMarkdownGuidePage = lazy(() =>
    import('@/pages/CmsMarkdownGuide/CmsMarkdownGuidePage.tsx').then(m => ({
        default: m.CmsMarkdownGuidePage,
    })),
);
const CmsZonesPage = lazy(() =>
    import('@/pages/CmsZones/CmsZonesPage.tsx').then(m => ({
        default: m.CmsZonesPage,
    })),
);
const CmsVisibilityPage = lazy(() =>
    import('@/pages/CmsVisibility/CmsVisibilityPage.tsx').then(m => ({
        default: m.CmsVisibilityPage,
    })),
);
const CmsVisibilityDocsPage = lazy(() =>
    import('@/pages/CmsVisibility/CmsVisibilityDocsPage.tsx').then(m => ({
        default: m.CmsVisibilityDocsPage,
    })),
);
const PartnersAndDataSource = lazy(() =>
    import('@/pages/PartnersAndDataSource/PartnersAndDataSource.tsx').then(m => ({ default: m.PartnersAndDataSource })),
);
const Support = lazy(() =>
    import('@/pages/Support/Support.tsx').then(m => ({ default: m.Support })),
);
const FAQPage = lazy(() =>
    import('@/pages/FAQ/FAQPage').then(m => ({ default: m.FAQPage })),
);
const GlossaryPage = lazy(() =>
    import('@/pages/Glossary/GlossaryPage').then(m => ({ default: m.GlossaryPage })),
);
const TakeCareerConfidenceSurvey = lazy(() =>
    import('@/pages/Surveys/CareerConfidence/TakeCareerConfidenceSurvey.tsx').then(m => ({ default: m.TakeCareerConfidenceSurvey })),
);
const TakeAssessment = lazy(() =>
    import('@/pages/Assessments/TakeAssessment/TakeAssessment.tsx').then(m => ({ default: m.TakeAssessment })),
);
const LandingPage = lazy(() =>
    import('@/pages/Landing/LandingPage.tsx').then(m => ({ default: m.LandingPage })),
);
const PartnerLandingPage = lazy(() =>
    import('@/pages/PartnerLanding/PartnerLandingPage.tsx').then(m => ({ default: m.PartnerLandingPage })),
);
const ReferralRockLandingPage = lazy(() =>
    import('@/pages/ReferralRockLanding/ReferralRockLandingPage.tsx').then(m => ({ default: m.ReferralRockLandingPage })),
);
const SurveyResultsPage = lazy(() =>
    import('@/pages/Surveys/Results/SurveyResultsPage.tsx').then(m => ({ default: m.SurveyResultsPage })),
);
const CareerLayout = lazy(() =>
    import('@/pages/Career/CareerLayout.tsx').then(m => ({ default: m.CareerLayout })),
);
const CareerOverviewPage = lazy(() =>
    import('@/pages/Career/Overview/CareerOverviewPage.tsx').then(m => ({ default: m.CareerOverviewPage })),
);
const CareerQualificationsPage = lazy(() =>
    import('@/pages/Career/Qualifications/CareerQualificationsPage.tsx').then(m => ({ default: m.CareerQualificationsPage })),
);
const CareerPathsPage = lazy(() =>
    import('@/pages/Career/Paths/CareerPathsPage.tsx').then(m => ({ default: m.CareerPathsPage })),
);
const CareerProgramsPage = lazy(() =>
    import('@/pages/Career/Programs/CareerProgramsPage.tsx').then(m => ({ default: m.CareerProgramsPage })),
);
const CareerJobsPage = lazy(() =>
    import('@/pages/Career/Jobs/CareerJobsPage.tsx').then(m => ({ default: m.CareerJobsPage })),
);
const CareerApprenticeshipsPage = lazy(() =>
    import('@/pages/Career/Apprenticeships/CareerApprenticeshipsPage.tsx').then(m => ({ default: m.CareerApprenticeshipsPage })),
);
const CareerInternshipsPage = lazy(() =>
    import('@/pages/Career/Internships/CareerInternshipsPage.tsx').then(m => ({ default: m.CareerInternshipsPage })),
);
const LoginErrorPage = lazy(() =>
    import('@/pages/LoginError/LoginErrorPage.tsx').then(m => ({ default: m.LoginErrorPage })),
);
const ChangePasswordMigrationPage = lazy(() =>
    import('@/pages/ChangePasswordMigration/ChangePasswordMigrationPage.tsx').then(m => ({ default: m.ChangePasswordMigrationPage })),
);
const CheckYourEmailPage = lazy(() =>
    import('@/pages/CheckYourEmail/CheckYourEmailPage.tsx').then(m => ({ default: m.CheckYourEmailPage })),
);
const AccountLinkingPage = lazy(() =>
    import('@/pages/AccountLinking/AccountLinkingPage.tsx').then(m => ({ default: m.AccountLinkingPage })),
);

const DashboardPage = lazy(() =>
    import('@/pages/Dashboard/DashboardPage.tsx')
        .then(({ DashboardPage }) => ({ default: DashboardPage })),
);

const AssessmentsPage = lazy(() =>
    import('@/pages/Assessments/AssessmentsPage.tsx')
        .then(({ AssessmentsPage }) => ({ default: AssessmentsPage })),
);

const AssessmentResultsPage = lazy(() =>
    import('@/pages/Assessments/Results/AssessmentResultsPage.tsx')
        .then(({ AssessmentResultsPage }) => ({ default: AssessmentResultsPage })),
);

const AssessmentResultsSummaryPage = lazy(() =>
    import('@/pages/Assessments/Results/Summary/AssessmentSummaryPage.tsx')
        .then(({ AssessmentSummaryPage }) => ({ default: AssessmentSummaryPage })),
);

const AssessmentResultsByCareerTypesPage = lazy(() =>
    import('@/pages/Assessments/RecommendedCareers/RecommendedCareersPage.tsx')
        .then(({ RecommendedCareersPage }) => ({ default: RecommendedCareersPage })),
);

const ResultsPerCareerTypePage = lazy(() =>
    import('@/pages/Assessments/RecommendedCareers/RecommendedCareerDetailsPage.tsx')
        .then(({ RecommendedCareerDetailsPage }) => ({ default: RecommendedCareerDetailsPage })),
);

const TopCareerMatchesPage = lazy(() =>
    import('@/pages/Assessments/RecommendedCareers/TopCareerMatchesPage/TopCareerMatchesPage.tsx')
        .then(({ TopCareerMatchesPage }) => ({ default: TopCareerMatchesPage })),
);

const ProfilePage = lazy(() =>
    import('@/pages/Profile/ProfilePage.tsx')
        .then(({ ProfilePage }) => ({ default: ProfilePage })),
);

const SharedLinksPage = lazy(() =>
    import('@/pages/Profile/SharedLinksPage.tsx')
        .then(({ SharedLinksPage }) => ({ default: SharedLinksPage })),
);

const LegacySkillsPage = lazy(() =>
    import('@/pages/LegacySkills/LegacySkillsPage.tsx')
        .then(({ LegacySkillsPage }) => ({ default: LegacySkillsPage })),
);

const SkillsPage = lazy(() =>
    import('@/pages/Skills/SkillsPage.tsx')
        .then(({ SkillsPage }) => ({ default: SkillsPage })),
);

const InferredSkillReviewPage = lazy(() =>
    import('@/pages/Skills/DocumentUpload/SkillReview/InferredSkillReviewPage')
        .then(({ InferredSkillReviewPage }) => ({ default: InferredSkillReviewPage })),
);

const UploadDocumentContent = lazy(() =>
    import('@/pages/Skills/DocumentUpload/UploadDocumentContent.tsx')
        .then(({ UploadDocumentContent }) => ({ default: UploadDocumentContent })),
);

const AddSkillsPage = lazy(() =>
    import('@/pages/Skills/AddSkillsPage.tsx')
        .then(({ AddSkillsPage }) => ({ default: AddSkillsPage })),
);

const SuggestedSkillsPage = lazy(() =>
    import('@/pages/Skills/SuggestedSkills/SuggestedSkillsPage.tsx')
        .then(({ SuggestedSkillsPage }) => ({ default: SuggestedSkillsPage })),
);

const SkillsBrainstormStartPage = lazy(() =>
    import('@/pages/Skills/Brainstorm/SkillsBrainstormStartPage.tsx')
        .then(({ SkillsBrainstormStartPage }) => ({ default: SkillsBrainstormStartPage })),
);

const SkillsBrainstormQuestionsPage = lazy(() =>
    import('@/pages/Skills/Brainstorm/SkillsBrainstormQuestionsPage.tsx')
        .then(({ SkillsBrainstormQuestionsPage }) => ({ default: SkillsBrainstormQuestionsPage })),
);

const JourneysLanding = lazy(() =>
    import('@/pages/Journeys/JourneysLanding')
        .then(({ JourneysLanding }) => ({ default: JourneysLanding })),
);
const JourneySelectCareer = lazy(() =>
    import('@/pages/Journeys/JourneySelectCareer')
        .then(({ JourneySelectCareer }) => ({ default: JourneySelectCareer })),
);
const JourneyDetail = lazy(() =>
    import('@/pages/Journeys/JourneyDetail')
        .then(({ JourneyDetail }) => ({ default: JourneyDetail })),
);

const LoginDemoPage = lazy(() =>
    import('@/pages/LoginDemo/LoginDemoPage')
        .then(({ LoginDemoPage }) => ({ default: LoginDemoPage })),
);

const CareerConfidenceSurveyDashboard = lazy(() =>
    import('@/pages/Surveys/CareerConfidence/CareerConfidenceSurveyDashboard.tsx')
        .then(({ CareerConfidenceSurveyDashboard }) => ({ default: CareerConfidenceSurveyDashboard })),
);

const CareerConfidenceCheckInPage = lazy(() =>
    import('@/pages/Surveys/CareerConfidence/CareerConfidenceCheckInPage.tsx')
        .then(({ CareerConfidenceCheckInPage }) => ({ default: CareerConfidenceCheckInPage })),
);

const CareerTreePage = lazy(() =>
    import('@/pages/Career/Paths/CareerTree/CareerHierarchy.tsx')
        .then(({ CareerHierarchy }) => ({ default: CareerHierarchy })),
);

const ExploreChallengesPage = lazy(() =>
    import('@/pages/Explore/Challenges/ExploreChallengesPage.tsx')
        .then(({ ExploreChallengesPage }) => ({ default: ExploreChallengesPage })),
);
const ExploreLessonsPage = lazy(() =>
    import('@/pages/Explore/Lessons/ExploreLessonsPage.tsx')
        .then(({ ExploreLessonsPage }) => ({ default: ExploreLessonsPage })),
);
const LessonPlayerPage = lazy(() =>
    import('@/pages/Explore/Lessons/LessonPlayerPage.tsx')
        .then(({ LessonPlayerPage }) => ({ default: LessonPlayerPage })),
);
const BadgeWalletPage = lazy(() =>
    import('@/pages/Badges/BadgeWalletPage.tsx')
        .then(({ BadgeWalletPage }) => ({ default: BadgeWalletPage })),
);
const BadgeDetailRoute = lazy(() =>
    import('@/pages/Badges/BadgeDetailRoute.tsx')
        .then(({ BadgeDetailRoute }) => ({ default: BadgeDetailRoute })),
);
const ExploreStoriesPage = lazy(() =>
    import('@/pages/Explore/Stories/ExploreStoriesPage.tsx')
        .then(({ ExploreStoriesPage }) => ({ default: ExploreStoriesPage })),
);
const StoryDetailPage = lazy(() =>
    import('@/pages/Explore/Stories/StoryDetailPage.tsx')
        .then(({ StoryDetailPage }) => ({ default: StoryDetailPage })),
);
const ExploreCareersPage = lazy(() =>
    import('@/pages/Explore/ExploreCareersPage.tsx')
        .then(({ ExploreCareersPage }) => ({ default: ExploreCareersPage })),
);

const ExploreInternshipsPage = lazy(() =>
    import('@/pages/Explore/Internships/ExploreInternshipsPage.tsx')
        .then(({ ExploreInternshipsPage }) => ({ default: ExploreInternshipsPage })),
);

const ExploreApprenticeshipsPage = lazy(() =>
    import('@/pages/Explore/Apprenticeships/ExploreApprenticeshipsPage.tsx')
        .then(({ ExploreApprenticeshipsPage }) => ({ default: ExploreApprenticeshipsPage })),
);

const ExploreRedesignProgramsPage = lazy(() =>
    import('@/pages/Explore/Programs/ExploreProgramsPage.tsx')
        .then(({ ExploreProgramsPage }) => ({ default: ExploreProgramsPage })),
);

const ExploreInstitutionsPage = lazy(() =>
    import('@/pages/Explore/Institutions/ExploreInstitutionsPage.tsx')
        .then(({ ExploreInstitutionsPage }) => ({ default: ExploreInstitutionsPage })),
);

const InstitutionDetailsContent = lazy(() =>
    import('@/pages/Explore/Institutions/InstitutionDetailsContent.tsx')
        .then(({ InstitutionDetailsContent }) => ({ default: InstitutionDetailsContent })),
);

const ExploreFundingPage = lazy(() =>
    import('@/pages/Explore/Funding/ExploreFundingPage.tsx')
        .then(({ ExploreFundingPage }) => ({ default: ExploreFundingPage })),
);

const FundingDetailsContent = lazy(() =>
    import('@/pages/Explore/Funding/FundingDetailsContent.tsx')
        .then(({ FundingDetailsContent }) => ({ default: FundingDetailsContent })),
);

const ExploreRedesignJobsPage = lazy(() =>
    import('@/pages/Explore/Jobs/ExploreJobsPage.tsx')
        .then(({ ExploreJobsPage }) => ({ default: ExploreJobsPage })),
);

const JobDetailsContent = lazy(() =>
    import('@/pages/Explore/Jobs/JobDetailsContent.tsx')
        .then(({ JobDetailsContent }) => ({ default: JobDetailsContent })),
);

const ProgramDetailsContent = lazy(() =>
    import('@/pages/Explore/Programs/ProgramDetailsContent.tsx')
        .then(({ ProgramDetailsContent }) => ({ default: ProgramDetailsContent })),
);

const ExploreCredentialsPage = lazy(() =>
    import('@/pages/Explore/Credentials/ExploreCredentialsPage.tsx')
        .then(({ ExploreCredentialsPage }) => ({ default: ExploreCredentialsPage })),
);

const CredentialDetailsContent = lazy(() =>
    import('@/pages/Explore/Credentials/CredentialDetailsContent.tsx')
        .then(({ CredentialDetailsContent }) => ({ default: CredentialDetailsContent })),
);

const CareerCredentialsPage = lazy(() =>
    import('@/pages/Career/Credentials/CareerCredentialsPage.tsx')
        .then(({ CareerCredentialsPage }) => ({ default: CareerCredentialsPage })),
);

const ApprenticeshipDetailsContent = lazy(() =>
    import('@/pages/Explore/Apprenticeships/ApprenticeshipDetailsContent.tsx')
        .then(({ ApprenticeshipDetailsContent }) => ({ default: ApprenticeshipDetailsContent })),
);

const AdminPage = lazy(() =>
    import('@/pages/Admin/AdminPage.tsx').then(m => ({ default: m.AdminPage })),
);

const DemoAdminPage = lazy(() =>
    import('@/pages/Admin/DemoAdminPage.tsx').then(m => ({ default: m.DemoAdminPage })),
);

const AdminRequestsPage = lazy(() =>
    import('@/pages/Admin/AdminRequestsPage.tsx').then(m => ({ default: m.AdminRequestsPage })),
);

const ZigBotFeedbackPage = lazy(() =>
    import('@/pages/Admin/ZigBotFeedbackPage.tsx').then(m => ({ default: m.ZigBotFeedbackPage })),
);

/** Path to redirect to when Auth0 sends email-verification success to root (e.g. /?success=true&code=success). */
const EMAIL_VERIFIED_REDIRECT_PATH = '/check-your-email';

/**
 * When Auth0 email verification redirects to root with success/result params, we redirect to a dedicated path.
 * Handles: success=true&code=success (verified); success=false&message=...used only once (link already used).
 */
function RootIndex({
    hasUser,
    isAuthenticatedUndefined,
}: {
    hasUser: boolean;
    isAuthenticatedUndefined: boolean;
}) {
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const onRoot = location.pathname === '/';
    const success = searchParams.get('success');
    const message = searchParams.get('message') ?? '';

    const isEmailVerificationSuccess = onRoot
        && success === 'true'
        && searchParams.get('code') === 'success';

    const isLinkAlreadyUsed = onRoot
        && success === 'false'
        && message.includes('used only once');

    if (isEmailVerificationSuccess) {
        return <Navigate to={`${EMAIL_VERIFIED_REDIRECT_PATH}?verified=1`} replace />;
    }
    if (isLinkAlreadyUsed) {
        return <Navigate to={`${EMAIL_VERIFIED_REDIRECT_PATH}?used=1`} replace />;
    }

    if (hasUser) {
        return <Navigate to="/dashboard" replace />;
    }

    const showReferralRockLanding = shouldShowReferralRockLanding(location.search);
    const showPartnerLanding = !showReferralRockLanding && shouldShowPartnerLanding(location.search);

    return (
        <PublicRoute>
            {showReferralRockLanding
                ? (
                        <ReferralRockLandingPage isFetching={isAuthenticatedUndefined} />
                    )
                : showPartnerLanding
                    ? (
                            <PartnerLandingPage isFetching={isAuthenticatedUndefined} />
                        )
                    : (
                            <LandingPage isFetching={isAuthenticatedUndefined} />
                        )}
        </PublicRoute>
    );
}
const CmsPage = lazy(() =>
    import('@/pages/Cms/CmsPage.tsx').then(({ CmsPage }) => ({ default: CmsPage })),
);

const ResourceCenterPage = lazy(() =>
    import('@/pages/ResourceCenter/ResourceCenterPage.tsx').then(({ ResourceCenterPage }) => ({ default: ResourceCenterPage })),
);

const ResourceCenterDetailPage = lazy(() =>
    import('@/pages/ResourceCenter/ResourceCenterDetailPage.tsx').then(({ ResourceCenterDetailPage }) => ({ default: ResourceCenterDetailPage })),
);

const ChangelogPage = lazy(() =>
    import('@/pages/Changelog/ChangelogPage.tsx').then(({ ChangelogPage }) => ({ default: ChangelogPage })),
);
const ChangelogDetailPage = lazy(() =>
    import('@/pages/Changelog/ChangelogDetailPage.tsx').then(({ ChangelogDetailPage }) => ({ default: ChangelogDetailPage })),
);

export const AppRoutes = () => {
    const { user, isFetching } = useUser();
    const [isAuthenticated, setIsAuthenticated] = useState<boolean | undefined>(undefined);
    const { featureFlags } = useFeatureFlags();
    const hydrated = useHydrated();

    /**
     * `isAuthenticated` is resolved in an effect below, and effects do not run on the server, so a
     * server render would sit at `undefined` forever and every consumer would show its loading
     * state — the landing page included, which is the one route where that is most visible.
     *
     * A server render is anonymous by construction (see useVisibilityContext), so the answer is
     * already known: not authenticated. The first client render of a server-rendered document says
     * the same thing so hydration matches, then the effect takes over. Documents that were not
     * server-rendered are unaffected — `hydrated` is true from their first render.
     */
    const resolvedIsAuthenticated = hydrated ? isAuthenticated : false;

    const featureFlagCmsPublicPages = featureFlags.FEATURE_FLAG_CMS_PUBLIC_PAGES === true;
    const featureFlagSkillsRedesign = featureFlags.FEATURE_FLAG_SKILLS_REDESIGN === true;

    useEffect(() => {
        if (user?.id && !isFetching) {
            setIsAuthenticated(true);
        }
        if (!user && !isFetching) {
            setIsAuthenticated(false);
        }
    }, [user, isFetching]);

    const privacyPublicPageRoute = (
        <PublicRoute>
            {featureFlagCmsPublicPages ? <CmsPage /> : <PrivacyPolicy showTitle={true} showLastModified={true} />}
        </PublicRoute>
    );
    const termsPublicPageRoute = (
        <PublicRoute>
            {featureFlagCmsPublicPages ? <CmsPage /> : <TermsAndConditions showTitle={true} showLastModified={true} />}
        </PublicRoute>
    );
    const faqPublicPageRoute = (
        <PublicRoute>
            {featureFlagCmsPublicPages ? <CmsPage /> : <FAQPage />}
        </PublicRoute>
    );
    const partnersPublicPageRoute = (
        <PublicRoute>
            {featureFlagCmsPublicPages ? <CmsPage /> : <PartnersAndDataSource />}
        </PublicRoute>
    );
    const glossaryPublicPageRoute = (
        <PublicRoute>
            {featureFlagCmsPublicPages ? <CmsPage /> : <GlossaryPage />}
        </PublicRoute>
    );
    const changelogPublicPageRoute = (
        <PublicRoute>
            <ChangelogPage />
        </PublicRoute>
    );
    const changelogDetailRoute = (
        <PublicRoute>
            <ChangelogDetailPage />
        </PublicRoute>
    );
    const cmsPagePublicRoute = (
        <PublicRoute>
            <CmsPage />
        </PublicRoute>
    );

    if (isAccountSetupIncomplete(user)) {
        return (
            <Routes>
                <Route
                    path=""
                    element={(
                        <ProtectedRoute isAuthenticated={resolvedIsAuthenticated}>
                            <GetStartedPage />
                        </ProtectedRoute>
                    )}
                />
                <Route
                    path="get-started"
                    element={(
                        <ProtectedRoute isAuthenticated={resolvedIsAuthenticated}>
                            <GetStartedPage />
                        </ProtectedRoute>
                    )}
                />
                <Route element={<Layout />}>
                    <Route path="privacy" element={<Navigate to="/privacy-policy" replace />} />
                    <Route path="terms" element={<Navigate to="/terms-of-use" replace />} />
                    <Route path="faq" element={<Navigate to="/faqs" replace />} />
                    <Route path="articles" element={<RedirectPreservingSearch to="/library" />} />
                    <Route path="articles/:slug" element={<RedirectLegacyArticleDetail />} />
                    <Route path="resources" element={<RedirectPreservingSearch to="/library" />} />
                    <Route path="resources/articles/:slug" element={<RedirectLegacyArticleDetail />} />
                    <Route path="support" element={<Navigate to="/contact-us" replace />} />
                    <Route path="privacy-policy" element={privacyPublicPageRoute} />
                    <Route path="terms-of-use" element={termsPublicPageRoute} />
                    <Route
                        path="contact-us"
                        element={(
                            <PublicRoute>
                                <Support />
                            </PublicRoute>
                        )}
                    />
                    <Route path="faqs" element={faqPublicPageRoute} />
                    <Route path="glossary" element={glossaryPublicPageRoute} />
                    <Route path="changelog" element={changelogPublicPageRoute} />
                    <Route path="changelog/:slug" element={changelogDetailRoute} />
                    <Route
                        path="library"
                        element={(
                            <PublicRoute>
                                <ResourceCenterPage />
                            </PublicRoute>
                        )}
                    />
                    <Route
                        path="library/articles/:slug"
                        element={(
                            <PublicRoute>
                                <ResourceCenterDetailPage />
                            </PublicRoute>
                        )}
                    />
                    <Route
                        path="partners-and-data-sources"
                        element={<Navigate to="/partners-and-vendors" replace />}
                    />
                    <Route path="partners-and-vendors" element={partnersPublicPageRoute} />
                    {featureFlagCmsPublicPages && (
                        <>
                            <Route path="cms/:slug" element={cmsPagePublicRoute} />
                            <Route path="partners" element={cmsPagePublicRoute} />
                        </>
                    )}
                </Route>
                <Route
                    path="login-demo"
                    element={(
                        <DemoRoute>
                            <LoginDemoPage />
                        </DemoRoute>
                    )}
                />
                <Route
                    path="logout"
                    element={<LogoutRedirect />}
                />
                <Route
                    path="login-error"
                    element={(
                        <PublicRoute>
                            <LoginErrorPage />
                        </PublicRoute>
                    )}
                />
                <Route
                    path="change-password-migration"
                    element={(
                        <PublicRoute>
                            <ChangePasswordMigrationPage />
                        </PublicRoute>
                    )}
                />
                <Route
                    path="link-accounts"
                    element={(
                        <PublicRoute>
                            <AccountLinkingPage />
                        </PublicRoute>
                    )}
                />
                <Route
                    path="check-your-email"
                    element={(
                        <PublicRoute>
                            <CheckYourEmailPage />
                        </PublicRoute>
                    )}
                />
                {/* Any other path (e.g. /dashboard, or a bookmark) would render nothing until setup is done. */}
                <Route path="*" element={<Navigate to="/get-started" replace />} />
            </Routes>
        );
    }

    return (
        <Routes>
            <Route
                path="login-error"
                element={(
                    <PublicRoute>
                        <LoginErrorPage />
                    </PublicRoute>
                )}
            />
            <Route
                path="change-password-migration"
                element={(
                    <PublicRoute>
                        <ChangePasswordMigrationPage />
                    </PublicRoute>
                )}
            />
            <Route
                path="link-accounts"
                element={(
                    <PublicRoute>
                        <AccountLinkingPage />
                    </PublicRoute>
                )}
            />
            <Route
                path="check-your-email"
                element={(
                    <PublicRoute>
                        <CheckYourEmailPage />
                    </PublicRoute>
                )}
            />
            <Route path="/" element={<Layout />}>
                <Route
                    index
                    element={(
                        <RootIndex
                            hasUser={!!user?.id}
                            isAuthenticatedUndefined={resolvedIsAuthenticated === undefined}
                        />
                    )}
                />
                <Route
                    path="assessments"
                    element={(
                        <ProtectedRoute isAuthenticated={resolvedIsAuthenticated}>
                            <AssessmentsPage />
                        </ProtectedRoute>
                    )}
                />
                <Route
                    path="assessment/:assessmentId/result/:assessmentType"
                    element={(
                        <ProtectedRoute isAuthenticated={resolvedIsAuthenticated}>
                            <AssessmentResultsPage />
                        </ProtectedRoute>
                    )}
                />
                <Route
                    path="assessment/:assessmentId/result/summary"
                    element={(
                        <ProtectedRoute isAuthenticated={resolvedIsAuthenticated}>
                            <AssessmentResultsSummaryPage />
                        </ProtectedRoute>
                    )}
                />
                <Route
                    path="assessments/results/career-types"
                    element={(
                        <ProtectedRoute isAuthenticated={resolvedIsAuthenticated}>
                            <AssessmentResultsByCareerTypesPage />
                        </ProtectedRoute>
                    )}
                />
                <Route
                    path="assessments/results/career-types/:id"
                    element={(
                        <ProtectedRoute isAuthenticated={resolvedIsAuthenticated}>
                            <ResultsPerCareerTypePage />
                        </ProtectedRoute>
                    )}
                />
                <Route
                    path="assessments/results/top-matches"
                    element={(
                        <ProtectedRoute isAuthenticated={resolvedIsAuthenticated}>
                            <TopCareerMatchesPage />
                        </ProtectedRoute>
                    )}
                />
                <Route
                    path="assessment/:assessmentId"
                    element={(
                        <ProtectedRoute isAuthenticated={resolvedIsAuthenticated}>
                            <TakeAssessment />
                        </ProtectedRoute>
                    )}
                />
                <Route
                    path="dashboard"
                    element={(
                        <ProtectedRoute isAuthenticated={resolvedIsAuthenticated}>
                            <DashboardPage />
                        </ProtectedRoute>
                    )}
                />
                <Route
                    path="career-confidence/check-in"
                    element={(
                        <ProtectedRoute isAuthenticated={resolvedIsAuthenticated}>
                            <CareerConfidenceCheckInPage />
                        </ProtectedRoute>
                    )}
                />
                <Route
                    path="get-started"
                    element={(
                        <ProtectedRoute isAuthenticated={resolvedIsAuthenticated}>
                            <RegisteredGetStartedRedirect user={user} />
                        </ProtectedRoute>
                    )}
                />
                <Route
                    path="profile"
                    element={(
                        <ProtectedRoute isAuthenticated={resolvedIsAuthenticated}>
                            <ProfilePage />
                        </ProtectedRoute>
                    )}
                />
                <Route
                    path="profile/shared-links"
                    element={(
                        <ProtectedRoute isAuthenticated={resolvedIsAuthenticated}>
                            <SharedLinksPage />
                        </ProtectedRoute>
                    )}
                />
                <Route
                    path="skills"
                    element={(
                        <ProtectedRoute isAuthenticated={resolvedIsAuthenticated}>
                            <SkillsProvider>
                                <Outlet />
                            </SkillsProvider>
                        </ProtectedRoute>
                    )}
                >
                    <Route
                        index
                        element={featureFlagSkillsRedesign ? <SkillsPage /> : <LegacySkillsPage />}
                    />
                    <Route element={<FeatureFlagOutletGate flagKey="FEATURE_FLAG_SKILLS_REDESIGN" redirectTo="/skills" />}>
                        <Route path="add" element={<AddSkillsPage />}>
                            <Route element={<FeatureAccessOutletGate featureKey="athena_skills_extraction" redirectTo="/skills/add" />}>
                                <Route path="upload-document" element={<UploadDocumentContent />} />
                            </Route>
                        </Route>
                        <Route element={<FeatureAccessOutletGate featureKey="athena_skills_extraction" redirectTo="/skills/add" />}>
                            <Route path="review-inferred-skills" element={<InferredSkillReviewPage />} />
                            <Route path="suggested" element={<SuggestedSkillsPage />} />
                            <Route element={<FeatureFlagOutletGate flagKey="FEATURE_FLAG_SKILLS_BRAINSTORM" redirectTo="/skills/add" />}>
                                <Route path="brainstorm" element={<SkillsBrainstormStartPage />} />
                                <Route path="brainstorm/questions" element={<SkillsBrainstormQuestionsPage />} />
                            </Route>
                        </Route>
                    </Route>
                </Route>
                <Route path="journeys" element={<Outlet />}>
                    <Route
                        index
                        element={(
                            <ProtectedRoute isAuthenticated={resolvedIsAuthenticated}>
                                <JourneysLanding />
                            </ProtectedRoute>
                        )}
                    />
                    <Route
                        path="new"
                        element={(
                            <ProtectedRoute isAuthenticated={resolvedIsAuthenticated}>
                                <JourneySelectCareer />
                            </ProtectedRoute>
                        )}
                    />
                    <Route
                        path=":journeyId"
                        element={(
                            <ProtectedRoute isAuthenticated={resolvedIsAuthenticated}>
                                <JourneyDetail />
                            </ProtectedRoute>
                        )}
                    />
                </Route>
                <Route
                    path="surveys/career-confidence/:id"
                    element={(
                        <ProtectedRoute isAuthenticated={resolvedIsAuthenticated}>
                            <CareerConfidenceSurveyDashboard />
                        </ProtectedRoute>
                    )}
                />
                <Route
                    path="surveys/career-confidence/:id/result/summary"
                    element={(
                        <ProtectedRoute isAuthenticated={resolvedIsAuthenticated}>
                            <SurveyResultsPage />
                        </ProtectedRoute>
                    )}
                />
                <Route
                    path="survey/:id"
                    element={(
                        <ProtectedRoute isAuthenticated={resolvedIsAuthenticated}>
                            <TakeCareerConfidenceSurvey />
                        </ProtectedRoute>
                    )}
                />
                <Route
                    path="explore"
                    element={(
                        <ProtectedRoute isAuthenticated={resolvedIsAuthenticated}>
                            <Outlet />
                        </ProtectedRoute>
                    )}
                >
                    <Route index element={<Navigate to="/explore/careers" replace />} />
                    <Route path="careers" element={<ExploreCareersPage />} />
                    <Route path="stories" element={<ExploreStoriesPage />} />
                    {/* A sibling of the listing rather than a child of it. `DetailSheet` opens on
                        any route param, so nesting this would put a story in the sheet instead of
                        on its own page. */}
                    <Route path="stories/:storyId" element={<StoryDetailPage />} />
                    <Route path="challenges" element={<ExploreChallengesPage />} />
                    <Route path="lessons" element={<ExploreLessonsPage />} />
                    <Route path="lessons/:courseId" element={<LessonPlayerPage />} />
                    <Route path="internships" element={<ExploreInternshipsPage />}>
                        <Route path=":jobId" element={<JobDetailsContent />} />
                    </Route>
                    <Route path="apprenticeships" element={<ExploreApprenticeshipsPage />}>
                        <Route path=":apprenticeshipId" element={<ApprenticeshipDetailsContent />} />
                        <Route path=":apprenticeshipSourceId/:apprenticeshipId" element={<ApprenticeshipDetailsContent />} />
                    </Route>
                    <Route path="programs" element={<ExploreRedesignProgramsPage />}>
                        <Route path=":programId" element={<ProgramDetailsContent />} />
                    </Route>
                    <Route path="certifications-and-licenses" element={<ExploreCredentialsPage />}>
                        <Route path=":credentialId" element={<CredentialDetailsContent />} />
                    </Route>
                    <Route path="institutions" element={<ExploreInstitutionsPage />}>
                        <Route path=":institutionId" element={<InstitutionDetailsContent />} />
                    </Route>
                    <Route path="funding" element={<ExploreFundingPage />}>
                        <Route path=":financialAidId" element={<FundingDetailsContent />} />
                    </Route>
                    <Route path="jobs" element={<ExploreRedesignJobsPage />}>
                        <Route path=":jobId" element={<JobDetailsContent />} />
                    </Route>
                </Route>
                <Route
                    path="badges"
                    element={(
                        <ProtectedRoute isAuthenticated={resolvedIsAuthenticated}>
                            <Outlet />
                        </ProtectedRoute>
                    )}
                >
                    <Route element={<FeatureFlagOutletGate flagKey="FEATURE_FLAG_BADGE_WALLET" />}>
                        <Route index element={<BadgeWalletPage />} />
                    </Route>
                    <Route path=":badgeId" element={<BadgeDetailRoute />} />
                </Route>
                <Route
                    path="careers/:occupationId"
                    element={(
                        <ProtectedRoute isAuthenticated={resolvedIsAuthenticated}>
                            <CareerLayout />
                        </ProtectedRoute>
                    )}
                >
                    <Route index element={<CareerOverviewPage />} />
                    <Route path="qualifications" element={<CareerQualificationsPage />} />
                    <Route path="paths" element={<CareerPathsPage />} />
                    <Route path="explore/programs" element={<CareerProgramsPage />}>
                        <Route path=":programId" element={<ProgramDetailsContent />} />
                    </Route>
                    <Route path="explore/certifications-and-licenses" element={<CareerCredentialsPage />}>
                        <Route path=":credentialId" element={<CredentialDetailsContent />} />
                    </Route>
                    <Route path="explore/jobs" element={<CareerJobsPage />}>
                        <Route path=":jobId" element={<JobDetailsContent />} />
                    </Route>
                    <Route path="explore/apprenticeships" element={<CareerApprenticeshipsPage />}>
                        <Route path=":apprenticeshipId" element={<ApprenticeshipDetailsContent />} />
                        <Route path=":apprenticeshipSourceId/:apprenticeshipId" element={<ApprenticeshipDetailsContent />} />
                    </Route>
                    <Route path="explore/internships" element={<CareerInternshipsPage />}>
                        <Route path=":jobId" element={<JobDetailsContent />} />
                    </Route>
                </Route>
                <Route
                    path="careers/:occupationId/career-tree"
                    element={(
                        <ProtectedRoute isAuthenticated={resolvedIsAuthenticated}>
                            <CareerTreePage />
                        </ProtectedRoute>
                    )}
                />
                <Route path="privacy" element={<Navigate to="/privacy-policy" replace />} />
                <Route path="terms" element={<Navigate to="/terms-of-use" replace />} />
                <Route path="faq" element={<Navigate to="/faqs" replace />} />
                <Route path="articles" element={<RedirectPreservingSearch to="/library" />} />
                <Route path="articles/:slug" element={<RedirectLegacyArticleDetail />} />
                <Route path="resources" element={<RedirectPreservingSearch to="/library" />} />
                <Route path="resources/articles/:slug" element={<RedirectLegacyArticleDetail />} />
                <Route path="support" element={<Navigate to="/contact-us" replace />} />
                <Route path="privacy-policy" element={privacyPublicPageRoute} />
                <Route path="terms-of-use" element={termsPublicPageRoute} />
                <Route
                    path="contact-us"
                    element={(
                        <PublicRoute>
                            <Support />
                        </PublicRoute>
                    )}
                />
                <Route path="faqs" element={faqPublicPageRoute} />
                <Route path="glossary" element={glossaryPublicPageRoute} />
                <Route path="changelog" element={changelogPublicPageRoute} />
                <Route path="changelog/:slug" element={changelogDetailRoute} />
                <Route
                    path="partners-and-data-sources"
                    element={<Navigate to="/partners-and-vendors" replace />}
                />
                <Route path="partners-and-vendors" element={partnersPublicPageRoute} />
                {featureFlagCmsPublicPages && (
                    <>
                        <Route path="cms/:slug" element={cmsPagePublicRoute} />
                        <Route path="partners" element={cmsPagePublicRoute} />
                    </>
                )}
                <Route
                    path="style-guide"
                    element={(
                        <NoProdRoute>
                            <PublicRoute>
                                <StyleGuide />
                            </PublicRoute>
                        </NoProdRoute>
                    )}
                />
                <Route
                    path="cms-markdown"
                    element={(
                        <NoProdRoute>
                            <PublicRoute>
                                <CmsMarkdownGuidePage />
                            </PublicRoute>
                        </NoProdRoute>
                    )}
                />
                <Route
                    path="cms-zones"
                    element={(
                        <NoProdRoute>
                            <PublicRoute>
                                <CmsZonesPage />
                            </PublicRoute>
                        </NoProdRoute>
                    )}
                />
                <Route
                    path="cms-visibility"
                    element={(
                        <NoProdRoute>
                            <PublicRoute>
                                <CmsVisibilityPage />
                            </PublicRoute>
                        </NoProdRoute>
                    )}
                />
                <Route
                    path="cms-visibility/docs"
                    element={(
                        <NoProdRoute>
                            <PublicRoute>
                                <CmsVisibilityDocsPage />
                            </PublicRoute>
                        </NoProdRoute>
                    )}
                />
                <Route
                    path="library"
                    element={(
                        <PublicRoute>
                            <ResourceCenterPage />
                        </PublicRoute>
                    )}
                />
                <Route
                    path="library/articles/:slug"
                    element={(
                        <PublicRoute>
                            <ResourceCenterDetailPage />
                        </PublicRoute>
                    )}
                />
                <Route
                    path="login"
                    element={(
                        <PublicRoute>
                            <LoginRedirect />
                        </PublicRoute>
                    )}
                />
                <Route
                    path="login-demo"
                    element={(
                        <DemoRoute>
                            <LoginDemoPage />
                        </DemoRoute>
                    )}
                />
                <Route
                    path="logout"
                    element={<LogoutRedirect />}
                />
                <Route
                    path="feature-access/admin"
                    element={(
                        <ProtectedRoute isAuthenticated={resolvedIsAuthenticated}>
                            <AdminPage />
                        </ProtectedRoute>
                    )}
                />
                <Route
                    path="skills/admin"
                    element={(
                        <ProtectedRoute isAuthenticated={resolvedIsAuthenticated}>
                            <DemoAdminPage />
                        </ProtectedRoute>
                    )}
                />
                <Route
                    path="feature-access/admin/requests"
                    element={(
                        <ProtectedRoute isAuthenticated={resolvedIsAuthenticated}>
                            <AdminRequestsPage />
                        </ProtectedRoute>
                    )}
                />
                <Route
                    path="zigbot-feedback"
                    element={(
                        <ProtectedRoute isAuthenticated={resolvedIsAuthenticated}>
                            <ZigBotFeedbackPage />
                        </ProtectedRoute>
                    )}
                />
                <Route path="*" element={<NotFound text="Page not found" />} />
            </Route>
        </Routes>
    );
};
